import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DetailFields, {
  FieldConfig,
  FieldsConfig,
  FieldValueFormatter
} from "@/components/layouts/DetailFields";
import {
  ContractColumn,
  ContractData,
  ContractTab
} from "@/pages/contracts/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import {
  mapSectionsFieldsConfig,
  useEntityDetailFieldsLayout
} from "@/hooks/useEntityFieldsConfig";
import {
  FINANCIALS_WRITE_PERMISSION,
  useHasPermission
} from "@/services/auth/AuthContext";
import { TAB_SEARCH_PARAM } from "@/components/layouts/DetailedView";
import {
  formatMonetaryDisplayValue,
  getTermsCurrencySign
} from "@/utils/numbers";
import { useAccountCurrency } from "@/services/hooks/useCurrencyConversion";
import {
  TERMS_FIELD_SUFFIX,
  termsFieldName
} from "@/entities/dataTables/termsCurrency";
import { ContractFieldUpdate } from "./ContractDetailedView";
import { omit } from "lodash";

// csmRenewalConfidenceLevel now lives only in the KPI header (editable there);
// keeping it out of Details avoids duplicate edit surfaces for the same field.
const HIDDEN_FIELD_KEYS = ["csmRenewalConfidenceLevel"];

// Each metric is stored twice: under this key in the account's currency, under its `Local` twin in the contract's.
const MONETARY_METRIC_FIELD_KEYS: ContractColumn[] = [
  ContractColumn.TCV,
  ContractColumn.ACV,
  ContractColumn.ARR,
  ContractColumn.MRR,
  ContractColumn.CARR,
  ContractColumn.CMRR
];

// Changing these alters a contract's financial history, so they are edited only
// through the Terms & Services flow that records the change, its effective date
// and its reason — here they read as plain text with a link into that flow.
// Both halves of each pair are listed: an account can surface the local-currency
// twin in Details, and editing it inline bypasses the same tracked flow.
// Derived from the pairing above so a new metric cannot arrive with only one half listed.
const TERMS_FLOW_ONLY_FIELD_KEYS = [
  ...MONETARY_METRIC_FIELD_KEYS,
  ...MONETARY_METRIC_FIELD_KEYS.map(termsFieldName)
];

// Detail-section fields that carry contract economics; editing them requires the
// financials role permission. Currency is not written through the terms flow, so
// it keeps inline editing and only the permission gate applies to it.
const FINANCIAL_FIELD_KEYS = [...TERMS_FLOW_ONLY_FIELD_KEYS, "currency"];

interface Props {
  contract?: ContractData;
  onUpdateField: ContractFieldUpdate;
  className?: string;
  containerClassName?: string;
  isQuickView?: boolean;
}

// Keeps the value reading exactly as the inline editor rendered it, so making
// the field read-only doesn't change the figure on screen.
const resolveReadOnlyFormatter = (
  existingConfig: FieldConfig,
  unknownText: string,
  // Set only for a terms row, whose figure is in the contract's currency rather than the account's.
  termsCurrencySymbol?: string
): FieldValueFormatter => {
  // A registry-supplied formatter wins, so the terms symbol never reaches it — such a field renders unformatted with no symbol either way.
  if ("formatter" in existingConfig && existingConfig.formatter) {
    const { formatter } = existingConfig;
    return (value) => {
      // readOnlyText formatters are typed to return a ReactNode; anything but a
      // string or number has no plain-text reading, so fall back rather than
      // rendering "[object Object]".
      const formatted = formatter(value);
      return typeof formatted === "string" || typeof formatted === "number"
        ? String(formatted)
        : unknownText;
    };
  }
  // With no terms symbol the registry's is used — empty here, which leaves getCurrencySign naming the account's own currency.
  const currencySymbol =
    termsCurrencySymbol ??
    ("currencySymbol" in existingConfig
      ? existingConfig.currencySymbol
      : undefined);
  return (value) => {
    if (value === null || value === undefined || value === "") {
      return unknownText;
    }
    return (
      formatMonetaryDisplayValue(String(value), {
        isMonetary: true,
        currencySymbol
      }) || unknownText
    );
  };
};

const ContractDetailFields: React.FC<Props> = ({
  contract,
  onUpdateField,
  className,
  containerClassName,
  isQuickView
}) => {
  const { t } = useTranslation();
  const canEditFinancials = useHasPermission(FINANCIALS_WRITE_PERMISSION);
  const { fieldsConfig: apiFieldsConfig, sections: apiSections } =
    useEntityDetailFieldsLayout(EntityType.CONTRACT, contract, undefined, {
      groupBySection: !isQuickView
    });
  // Only the id and terms currency are read below; depending on the whole contract would rebuild every config on each save.
  const contractId = contract?.id;
  const rawCurrency = contract?.currency;

  // The account row sits directly above the terms row, so the terms sign has to
  // be one the account's own is not: CAD's narrow symbol is `$`, the same as a
  // USD account's, which left the two figures indistinguishable.
  const accountCurrency = useAccountCurrency();

  // Resolved from Intl, not currency-list's symbol_native, which disagrees with the contracts table on INR, JPY and CNY.
  const termsCurrencySymbol = rawCurrency
    ? getTermsCurrencySign(rawCurrency, accountCurrency)
    : undefined;

  const { fieldsConfig, sections } = useMemo(() => {
    // Run on every section too, so a financial field an admin moves into a
    // section keeps its permission lock and terms-flow row
    const applyContractRules = (config: FieldsConfig): FieldsConfig => {
      const visibleConfig = omit(config, HIDDEN_FIELD_KEYS);
      const lockedConfig: FieldsConfig = { ...visibleConfig };

      if (!canEditFinancials) {
        for (const fieldKey of FINANCIAL_FIELD_KEYS) {
          if (lockedConfig[fieldKey]) {
            lockedConfig[fieldKey] = {
              ...lockedConfig[fieldKey],
              disabled: true,
              disabledTooltipText: t("common.noEditPermission")
            };
          }
        }
      }

      const termsTabPath = contractId
        ? `/contracts/${contractId}?${TAB_SEARCH_PARAM}=${ContractTab.TERMS}`
        : undefined;

      for (const fieldKey of TERMS_FLOW_ONLY_FIELD_KEYS) {
        const existingConfig = lockedConfig[fieldKey];
        if (!existingConfig) continue;
        // A terms row reads in the currency the contract was signed in; its twin is normalized to the account's.
        const isTermsRow = fieldKey.endsWith(TERMS_FIELD_SUFFIX);
        // Spread rather than rebuild so registry-supplied props (label, icon,
        // helpText, layout, pairedWith, keepOrder, fieldKey) are not dropped as
        // the registry grows. Properties arriving via the spread are not
        // excess-property-checked against the target variant — ones written here
        // literally still are — so editing-only props ride along unchecked; this
        // type's renderer reads none of them.
        lockedConfig[fieldKey] = {
          ...existingConfig,
          type: "textWithHoverAction",
          actionLabel: t("contracts.detailedView.editTerms"),
          // Every one of these rows shows the same "Edit terms" label, so without
          // the field name they are indistinguishable to anyone reading the
          // accessible name rather than the row. Interpolated rather than
          // concatenated so word order stays the translator's to decide, and
          // falling back to the same key DetailFields uses for the visible label,
          // so the two never disagree.
          actionAriaLabel: t("contracts.detailedView.editTermsFor", {
            field:
              existingConfig.label || t(`contracts.contractView.${fieldKey}`)
          }),
          // A user without the financials permission cannot edit terms either, so
          // they get the value with no edit affordance at all.
          to: canEditFinancials ? termsTabPath : undefined,
          // On the contract's own page this is a tab switch, and every other tab
          // switch there replaces: pushing would leave Back on a tab-less URL the
          // page's sync effect ignores. In the quick-view drawer the current entry
          // is whichever page opened the drawer, so replacing would drop that page
          // from history — navigating away from it is a push.
          replace: !isQuickView,
          formatter: resolveReadOnlyFormatter(
            existingConfig,
            t("unknown"),
            isTermsRow ? termsCurrencySymbol : undefined
          )
        };
      }

      return lockedConfig;
    };

    return {
      fieldsConfig: applyContractRules(apiFieldsConfig),
      sections: mapSectionsFieldsConfig(apiSections, applyContractRules)
    };
  }, [
    apiFieldsConfig,
    apiSections,
    canEditFinancials,
    contractId,
    isQuickView,
    t,
    termsCurrencySymbol
  ]);

  return (
    <DetailFields
      entity={contract}
      fieldsConfig={fieldsConfig}
      sections={sections}
      onUpdateField={onUpdateField}
      tPrefix="contracts.contractView"
      className={className}
      containerClassName={containerClassName}
      isQuickView={isQuickView}
    />
  );
};

export default ContractDetailFields;
